/**
 * The incident report = the unit condition assessment (SM_UnitConditionAssessments), exactly as the
 * design reference shows it: five numbered groups in plain words, in the crew's language, each answer
 * one column of the list. The words the crew writes are kept as written; the office sees an English
 * translation beside them.
 */
import { useTranslation } from 'react-i18next';
import {
  ASSESSMENT_FOLLOW_UP_CATEGORIES,
  CONDITION_CATEGORIES,
  EFFECTS,
  OBSERVED_CHIPS,
  PRIORITIES,
  SEVERITIES,
  type AssessmentForm,
  type ConditionCategory,
} from '@shared/closeout';
import { PhotoPicker, type PhotoItem } from './Photos';
import { YesNo } from './bits';

/** The reference's plain label for a stored category (c1…c15 in the list's own order). */
export const catKey = (c: ConditionCategory) => `finish.report.cat.c${CONDITION_CATEGORIES.indexOf(c) + 1}`;

export function IncidentReport({
  value,
  onChange,
  photos,
  onPhotos,
}: {
  value: AssessmentForm;
  onChange: (next: AssessmentForm) => void;
  photos: PhotoItem[];
  onPhotos: (next: PhotoItem[]) => void;
}) {
  const { t } = useTranslation();
  const set = <K extends keyof AssessmentForm>(k: K, v: AssessmentForm[K]) => onChange({ ...value, [k]: v });
  const u = value;
  return (
    <div id="incident-fields" data-testid="incident-report">
      <p className="help">{t('finish.report.intro')}</p>
      <div className="inc-grid" style={{ marginTop: 12 }}>
        <div className="inc-h">
          <span className="num" aria-hidden="true">
            1
          </span>
          <h3 id="inc-h1">{t('finish.report.h1')}</h3>
        </div>
        <div className="field">
          <label htmlFor="i-main">{t('finish.report.main')}</label>
          <select id="i-main" className="input" value={u.primary} onChange={(e) => set('primary', e.target.value as ConditionCategory)}>
            {CONDITION_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t(catKey(c))}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="i-other">{t('finish.report.other')}</label>
          <input id="i-other" className="input" value={u.otherDescription} onChange={(e) => set('otherDescription', e.target.value)} />
        </div>
        <fieldset className="field span">
          <legend className="eyebrow">{t('finish.report.also')}</legend>
          <div className="chips dense" role="group" aria-label={t('finish.report.also')}>
            {OBSERVED_CHIPS.filter((c) => c !== u.primary).map((c) => {
              const on = u.observed.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  className="chip multi"
                  aria-pressed={on}
                  onClick={() => set('observed', on ? u.observed.filter((x) => x !== c) : [...u.observed, c])}
                >
                  {t(catKey(c))}
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="inc-h">
          <span className="num" aria-hidden="true">
            2
          </span>
          <h3 id="inc-h2">{t('finish.report.h2')}</h3>
        </div>
        <div className="radio-cards span" role="radiogroup" aria-labelledby="inc-h2">
          {SEVERITIES.map((s) => (
            <label key={s}>
              <input type="radio" name="sev" value={s} checked={u.severity === s} onChange={() => set('severity', s)} data-testid={`sev-${s}`} />
              <span>
                {t(`finish.report.sev.${s}`)}
                <small>{t(`finish.report.sevHelp.${s}`)}</small>
              </span>
            </label>
          ))}
        </div>
        <div className="span" style={{ display: 'grid' }}>
          <YesNo id="yn-danger" label={t('finish.report.ynDanger')} value={u.immediateDanger} onChange={(v) => set('immediateDanger', v)} />
          <YesNo id="yn-stopped" label={t('finish.report.ynStopped')} value={u.serviceStopped} onChange={(v) => set('serviceStopped', v)} />
          <YesNo id="yn-super" label={t('finish.report.ynSuper')} value={u.supervisorContacted} onChange={(v) => set('supervisorContacted', v)} />
          <YesNo id="yn-mgmt" label={t('finish.report.ynMgmt')} value={u.buildingManagementContacted} onChange={(v) => set('buildingManagementContacted', v)} />
          <YesNo id="yn-911" label={t('finish.report.yn911')} value={u.emergencyContacted} onChange={(v) => set('emergencyContacted', v)} />
        </div>

        <div className="inc-h">
          <span className="num" aria-hidden="true">
            3
          </span>
          <h3>{t('finish.report.h3')}</h3>
        </div>
        <div className="field span">
          <label htmlFor="idesc">{t('finish.report.desc')}</label>
          <textarea
            id="idesc"
            className="input"
            required
            aria-required="true"
            aria-describedby="idesc-help"
            placeholder={t('finish.report.descPlaceholder')}
            value={u.observationSummary}
            onChange={(e) => set('observationSummary', e.target.value)}
          />
          <p className="help" id="idesc-help">
            {t('finish.report.transHelp')}
          </p>
        </div>
        <div className="field">
          <label htmlFor="i-areas">{t('finish.report.areas')}</label>
          <input id="i-areas" className="input" value={u.areasAffected} onChange={(e) => set('areasAffected', e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="i-effect">{t('finish.report.effect')}</label>
          <select id="i-effect" className="input" value={u.effect} onChange={(e) => set('effect', e.target.value as AssessmentForm['effect'])}>
            {EFFECTS.map((x) => (
              <option key={x} value={x}>
                {t(`finish.report.effects.${x}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="field span">
          <label htmlFor="i-esc">{t('finish.report.esc')}</label>
          <textarea id="i-esc" className="input" style={{ minHeight: 64 }} value={u.escalationNotes} onChange={(e) => set('escalationNotes', e.target.value)} />
        </div>

        <div className="inc-h">
          <span className="num" aria-hidden="true">
            4
          </span>
          <h3>{t('finish.report.h4')}</h3>
        </div>
        <div className="field">
          <label htmlFor="i-fu">{t('finish.report.fu')}</label>
          <select id="i-fu" className="input" value={u.followUpRecommended ? 'yes' : 'no'} onChange={(e) => set('followUpRecommended', e.target.value === 'yes')}>
            <option value="yes">{t('finish.yes')}</option>
            <option value="no">{t('finish.no')}</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="i-fucat">{t('finish.report.fuCat')}</label>
          <select
            id="i-fucat"
            className="input"
            disabled={!u.followUpRecommended}
            value={u.followUpCategory}
            onChange={(e) => set('followUpCategory', e.target.value as AssessmentForm['followUpCategory'])}
          >
            {ASSESSMENT_FOLLOW_UP_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t(`finish.report.fc.${c}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="i-fupri">{t('finish.report.fuPri')}</label>
          <select
            id="i-fupri"
            className="input"
            disabled={!u.followUpRecommended}
            value={u.followUpPriority}
            onChange={(e) => set('followUpPriority', e.target.value as AssessmentForm['followUpPriority'])}
          >
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {t(`finish.report.fp.${p}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="i-furec">{t('finish.report.fuRec')}</label>
          <input
            id="i-furec"
            className="input"
            placeholder={t('finish.report.fuRecPlaceholder')}
            value={u.recommendedFollowUp}
            onChange={(e) => set('recommendedFollowUp', e.target.value)}
          />
        </div>

        <div className="inc-h">
          <span className="num" aria-hidden="true">
            5
          </span>
          <h3>{t('finish.report.h5')}</h3>
        </div>
        <div className="field span">
          {/* 17 Oct 2026: these stay on the phone until Record — picked, they are "ready", never "added" */}
          <PhotoPicker id="photo-in" testid="report-photos" items={photos} onChange={onPhotos} legend={t('finish.photos')} pickedWords={(count) => t('finish.photoKept', { count })} />
          <label className="switch" style={{ marginTop: 8 }}>
            <input id="i-release" type="checkbox" checked={u.tenantAgreedPhotos} onChange={(e) => set('tenantAgreedPhotos', e.target.checked)} />
            <span>{t('finish.report.release')}</span>
          </label>
          <p className="help">{t('finish.report.photoHelp')}</p>
          <label htmlFor="i-photo-notes">{t('finish.report.photoNotes')}</label>
          <input id="i-photo-notes" className="input" value={u.photoNotes} onChange={(e) => set('photoNotes', e.target.value)} />
        </div>
      </div>
    </div>
  );
}
